import React from "react";
import type { AttachmentItem } from "../../assembly/input/attachments";
import { UPLOAD_STATUS_TEXT } from "../../shell/connText";

/**
 * components/composer/ComposerUploadBar.tsx —— 草稿附件栏（**dsh 形态 rail**）
 *
 * ★20261011 大王决策（逐字「附件形态 64px 高横向 rail（文件卡 240px＋大写扩展名）参照 dsh」）：
 *   形态由**行式清单**（旧树 `ComposerUploadBar.vue` 的 name/size/status/重试/↑↓/✕ 一行到底）
 *   改为 **dsh 的 64px 高横向 rail**：每件＝一张 **240px** 宽卡，卡内**扩展名大写**徽标（`PDF`/`PNG`/`MD`…）
 *   ＋文件名（单行省略）＋大小；overlay 移除钮。
 *   依据件＝`dispatch-waveA/incoming/IN-DSH/dsh_输入区调研.md`（224 实跑件值）。
 *
 * 保留（不得丢，旧树／本项目自有语义，dsh 卡没有）：
 *   ① **显式排序**（升级方案 §2 批 U-1d「可删可排序，排序是显式动作」；设计件 §7.3(a)「附件选择顺序保留」）
 *      ⇒ ↑↓ 钮保留（dsh 卡的拖拽排序未纳入本批）
 *   ② **重试**（上传失败可重试；旧树行内 `重试`）
 *   ③ a11y：每件＝真按钮＋文本等价（aria-label 逐件带文件名）；动作钮触屏常显（不依赖 hover，§10.5）
 * 纪律（组件三戒）：不发请求（动作全部上抛）、不解析原始载荷、不横向读别人状态。
 */

function statusText(item: AttachmentItem): string {
  return UPLOAD_STATUS_TEXT[item.status] || item.status;
}
function sizeText(bytes: number): string {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
/** ★20261011：**大写扩展名**（dsh 卡左上角徽标口径）——无扩展名 ⇒ `FILE` */
export function extBadge(name: string): string {
  const base = (name || "").split("/").pop() ?? "";
  const i = base.lastIndexOf(".");
  if (i <= 0 || i === base.length - 1) return "FILE";
  return base.slice(i + 1).toUpperCase().slice(0, 5);
}

export interface ComposerUploadBarProps {
  /** 附件条目（`useComposerUpload.items` 快照：{id,name,size,status,fileId,error}） */
  items: readonly AttachmentItem[];
  /** 等待态可见集（B-21②：仅超过 250ms 门槛的 id 在此 ⇒ 渲染 spinner 字样） */
  waitIds: readonly string[];
  onRemove: (id: string) => void;
  onMove: (payload: { id: string; delta: number }) => void;
  onRetry: (id: string) => void;
}

export function ComposerUploadBar(props: ComposerUploadBarProps): React.ReactElement | null {
  const rows = props.items.map((item, i) => ({
    item,
    first: i === 0,
    last: i === props.items.length - 1,
    waiting: props.waitIds.includes(item.id),
  }));
  if (!rows.length) return null;
  return (
    /* dsh 形态：横向 rail（64px 高、卡片 240px、横向滚动）；旧行式清单类名 `xg-upload-row*` 同时保留
       （既有断言/样式过渡面），新增 rail 锚 `composer-attach-rail`（IN1 判据）。 */
    <div
      className="xg-attach-rail xg-upload-bar"
      data-testid="composer-upload-bar"
      data-rail="composer-attach-rail"
      role="list"
      aria-label="草稿附件"
    >
      {rows.map(({ item, first, last, waiting }) => (
        <div
          className="xg-attach-card xg-upload-row"
          role="listitem"
          key={item.id}
          data-testid={`upload-row-${item.id}`}
          data-status={item.status}
        >
          {/* 扩展名徽标（**大写**；dsh 卡左上角） */}
          <span className="xg-attach-card__ext" data-testid={`attach-ext-${item.id}`} aria-hidden="true">
            {extBadge(item.name)}
          </span>
          <span className="xg-attach-card__body">
            <span className="xg-upload-row__name" title={item.name}>{item.name}</span>
            <span className="xg-attach-card__meta">
              <span className="xg-upload-row__size">{sizeText(item.size)}</span>
              <span className="xg-upload-row__status" data-state={item.status}>{statusText(item)}</span>
            </span>
          </span>
          {waiting ? (
            <span className="xg-upload-row__spinner" data-testid="upload-waiting" role="status">处理中…</span>
          ) : null}
          {item.status === "error" && item.error ? (
            <span className="xg-upload-row__error" title={item.error}>{item.error}</span>
          ) : null}
          {/* 卡内动作（触屏常显）：重试／上移／下移／移除 */}
          <span className="xg-attach-card__acts">
            {item.status === "error" ? (
              <button
                type="button"
                className="xg-upload-row__act"
                aria-label={`重试上传 ${item.name}`}
                onClick={() => props.onRetry(item.id)}
              >重试</button>
            ) : null}
            <button
              type="button"
              className="xg-upload-row__act"
              aria-label={`上移附件 ${item.name}`}
              disabled={first}
              onClick={() => props.onMove({ id: item.id, delta: -1 })}
            >↑</button>
            <button
              type="button"
              className="xg-upload-row__act"
              aria-label={`下移附件 ${item.name}`}
              disabled={last}
              onClick={() => props.onMove({ id: item.id, delta: 1 })}
            >↓</button>
            <button
              type="button"
              className="xg-upload-row__act xg-upload-row__act--x"
              aria-label={`移除附件 ${item.name}`}
              onClick={() => props.onRemove(item.id)}
            >✕</button>
          </span>
        </div>
      ))}
    </div>
  );
}

export default ComposerUploadBar;
